<template>
  <div class="reservation-display">
    <!-- Reservation Status -->
    <p class="px-2 mb-0">
      <label class="badge" :class="startedStatus.class" v-if="startedStatus">
        {{ startedStatus.text }}
      </label>
      <label class="badge" :class="completedStatus.class" v-if="completedStatus">
        {{ completedStatus.text }}
      </label>
      <label class="badge badge-info" v-if="reservation.refunded">
        Refunded
      </label>
      <label class="badge badge-primary" v-if="reservation.LotUuid !== reservation.DestinationLotUuid && !reservation.maintenance">
        One Way Trip
      </label>
      <label class="badge badge-danger" v-if="reservation.maintenance && reservation.LotUuid === reservation.DestinationLotUuid">Maintenance Reservation</label>
      <label class="badge badge-primary" v-if="reservation.LotUuid !== reservation.DestinationLotUuid && reservation.maintenance">
        Lot Relocation
      </label>
      <template v-if="!reservation.maintenance">
        <label class="badge badge-default" v-if="reservation.FinanceCode">Cost Code</label>
        <label class="badge badge-purple" v-if="!reservation.isPersonal && !reservation.FinanceCode">Business Reservation</label>
        <label class="badge badge-warning" v-if="reservation.isPersonal && !reservation.FinanceCode">Personal Reservation</label>
      </template>
      <label class="badge badge-success" v-if="reservation.acknowledged">Acknowledged</label>
      <label class="badge badge-default" v-if="!reservation.acknowledged">Not Sent</label>
    </p>
    <accordian-display
      :icon="'fa-calendar-alt'"
      :title="'Reservation Details'"
      :defaultOpen="true">
      <!-- Reservation ID -->
      <tr>
        <td class="font-weight-bold" width="40%">Reservation ID</td>
        <td>
          <router-link :to="{ path: '/fleet/reservations/' + reservation.uuid }">
            #{{ reservation.reservationId }}
          </router-link>
        </td>
      </tr>
      <!-- Billing Status -->
      <tr v-if="!reservation.maintenance">
        <td class="font-weight-bold">Billing Status</td>
        <td>
          <span :class="billingStatus.class" v-if="billingStatus">
            <span class="fa-stack">
              <span class="fa fa-circle fa-stack-2x"></span>
              <span class="fa fa-stack-1x fa-inverse" :class="billingStatus.icon"></span>
            </span>
            {{ billingStatus.text }}
          </span>
        </td>
      </tr>
      <!-- Reservation Details -->
      <tr>
        <td class="font-weight-bold">Reservation Created</td>
        <td>
          {{ reservation.createdAt | date_format(reservation.Lot.timezone) }}
        </td>
      </tr>
      <tr>
        <td class="font-weight-bold">Driver</td>
        <td>
          <router-link :to="{ path: '/membership/members/' + reservation.Account.uuid }">
            {{ reservation.Account.dropdownLabel }}
          </router-link>
        </td>
      </tr>
      <tr v-if="reservation.Account.cardId">
        <td class="font-weight-bold">SmartCard</td>
        <td>
          {{ reservation.Account.Smartcard.cardNumber }} -   {{ reservation.Account.Smartcard.chipId }}
        </td>
      </tr>
      <tr v-if="reservation.Account.businessName && !reservation.maintenance">
        <td class="font-weight-bold">Business</td>
        <td>
          {{ reservation.Account.businessName }}
        </td>
      </tr>
      <tr>
        <td class="font-weight-bold">Car</td>
        <td>
          <router-link :to="'/fleet/vehicles/' + reservation.Car.uuid">
            {{ reservation.Car.labelNoLot }}
          </router-link>
        </td>
      </tr>
      <tr>
        <td class="font-weight-bold">Lot</td>
        <td>
          {{ reservation.Lot.dropdownLabel }}
        </td>
      </tr>
      <tr v-if="reservation.LotUuid !== reservation.DestinationLotUuid">
        <td class="font-weight-bold">Destination Lot</td>
        <td>
          {{ reservation.DestinationLot.dropdownLabel }}
        </td>
      </tr>
      <tr>
        <td colspan="2" class="bg-success text-white">
          <span class="fa fa-sign-in-alt fa-fw"></span> Start Times
        </td>
      </tr>
      <tr>
        <td class="font-weight-bold">Scheduled for</td>
        <td>
          {{ reservation.startDate | date_format(reservation.Lot.timezone) }}
        </td>
      </tr>
      <tr v-if="reservation.completedStartDate">
        <td class="font-weight-bold">Member Entered</td>
        <td class="rt-cell thin-cell last-rt-cell col-xs-8 green">
          {{ reservation.completedStartDate | date_format(reservation.Lot.timezone) }}
        </td>
      </tr>
      <tr>
        <td colspan="2" class="bg-danger text-white">
          <span class="fa fa-sign-out-alt fa-fw"></span> End Times
        </td>
      </tr>
      <tr>
        <td class="font-weight-bold">Expires at</td>
        <td>
          {{ reservation.endDate | date_format(reservation.Lot.timezone) }}
        </td>
      </tr>
      <tr v-if="reservation.completedEndDate">
        <td class="font-weight-bold">Reservation Completed</td>
        <td :class="late ? 'text-danger' : 'text-success'">
          {{ reservation.completedEndDate | date_format(reservation.Lot.timezone) }}
          <span class="badge badge-danger" v-if="late">{{ late }} minutes late</span>
        </td>
      </tr>
      <tr>
        <td colspan="2" class="bg-info text-white">
          <span class="fa fa-dollar-sign fa-fw"></span> Costs
        </td>
      </tr>
      <tr v-if="!reservation.maintenance">
        <td class="font-weight-bold">Extras</td>
        <td v-if="reservation.extras && reservation.extras.length">
          <span v-for="extra in reservation.extras" :key="extra.uuid">
            &#9642; {{ extra.name }}<br/>
          </span>
        </td>
        <td v-else>
          <em>None selected</em>
        </td>
      </tr>
      <tr v-if="!reservation.maintenance">
        <td class="font-weight-bold">Included KMs</td>
        <td>
          {{ includedKms }}
        </td>
      </tr>
      <tr v-if="reservation.completed">
        <td class="font-weight-bold">Completed KMs</td>
        <td>
          {{ reservation.completedKms }}km
        </td>
      </tr>
      <tr v-if="reservation.completed && !reservation.maintenance">
        <td class="font-weight-bold">Late Fee</td>
        <td>
          {{ reservation.lateFee | to_dollar_amount }}
        </td>
      </tr>
      <tr v-if="reservation.completed && !reservation.maintenance">
        <td class="font-weight-bold">KM Fee</td>
        <td>
          {{ reservation.kmFee | to_dollar_amount }}
        </td>
      </tr>
      <tr v-if="reservation.AdminResponsible">
        <td class="font-weight-bold">Admin Responsible</td>
        <td>
          <router-link :to="{ path: `/membership/members/${reservation.AdminResponsible.uuid}`}">
            {{ reservation.AdminResponsible.dropdownLabel }}
          </router-link>
        </td>
      </tr>
      <tr v-if="reservation.ReservationPromo">
        <td class="font-weight-bold">Promotional Code</td>
        <td>
          <label class="label label-info">
            <strong>{{ reservation.ReservationPromo.code }} - {{ reservation.ReservationPromo.percentDiscount }}% off</strong>
          </label>
        </td>
      </tr>
      <tr v-if="reservation.percentDiscount">
        <td class="font-weight-bold">Percent Discount</td>
        <td>
          {{ reservation.percentDiscount }}%
        </td>
      </tr>
      <tr v-if="reservation.FinanceCode">
        <td class="font-weight-bold">Cost Code</td>
        <td>
          {{ reservation.FinanceCode.code }} - {{ reservation.FinanceCode.description }}
        </td>
      </tr>
      <tr v-if="!reservation.maintenance">
        <td class="font-weight-bold">Reservation Charge</td>
        <td>
          {{ reservation.actualCost || reservation.quotedCost | to_dollar_amount }}
        </td>
      </tr>
      <tr v-if="reservation.percentDiscount">
        <td class="font-weight-bold">Amount Saved</td>
        <td>
          {{ reservation.preDiscountCost - (reservation.actualCost || reservation.quotedCost) | to_dollar_amount }}
        </td>
      </tr>
      <tr v-if="reservation.memo">
        <td class="font-weight-bold">Memo</td>
        <td>
          {{ reservation.memo }}
        </td>
      </tr>
    </accordian-display>
  </div>
</template>

<script>
import AccordianDisplay from '@/components/displays/AccordianDisplay'
import moment from 'moment-timezone'

export default {
  name: 'ReservationDisplay',
  props: {
    data: {
      type: Object
    }
  },
  components: {
    AccordianDisplay
  },
  computed: {
    includedKms () {
      if (this.reservation.extras) {
        for (let extra of this.reservation.extras) {
          if (extra.unlimitedKms && extra.unlimitedKms.toString() === 'true') {
            return 'Unlimited'
          }
        }
      }
      let days = moment(this.reservation.endDate).diff(moment(this.reservation.startDate), 'days')
      if (this.reservation.Organization.defaultIncludedKms && days === 0) {
        days = 1
      }
      return (days * this.reservation.includeKms) + 'km'
    },
    late () {
      if (this.reservation.completedEndDate) {
        let sEnd = moment(this.reservation.endDate)
        let aEnd = moment(this.reservation.completedEndDate)
        let mDiff = aEnd.diff(sEnd, 'minutes')
        if (mDiff > 5) return mDiff
      }
      return false
    },
    startedStatus () {
      if (this.reservation.started) {
        return { class: 'badge-info', text: 'Started' }
      } else if (moment(this.reservation.startDate).isAfter(moment())) {
        return { class: 'badge-default', text: 'Upcoming' }
      }
    },
    completedStatus () {
      if (this.reservation.completed) {
        if (this.reservation.cancelled) {
          return { class: 'badge-warning', text: 'Completed Early' }
        }
        return { class: 'badge-success', text: 'Completed' }
      } else if (this.reservation.cancelled) {
        return { class: 'badge-danger', text: 'Cancelled' }
      }
    },
    billingStatus () {
      let textClass, icon, text
      let startedStatus = this.startedStatus
      if (!this.reservation.refunded && (this.reservation.completed || this.reservation.cancelled)) {
        textClass = 'text-success'
        icon = 'fa-dollar-sign'
        text = 'Billed'
      } else if (!this.reservation.completed && !this.reservation.cancelled) {
        if (startedStatus && startedStatus.text.includes('Upcoming')) {
          textClass = 'text-warning'
          icon = 'fa-clock'
          text = 'Billed waiting to be started'
        } else if (startedStatus && startedStatus.text.includes('Started')) {
          textClass = 'text-warning'
          icon = 'fa-clock'
          text = 'Waiting Trip Data'
        } else {
          textClass = 'text-muted'
          icon = 'fa-clock'
          text = 'Billed and not used'
        }
      } else if (this.reservation.cancelled && !this.reservation.refunded) {
        textClass = 'text-danger'
        icon = 'fa-ban'
        text = 'Billed and cancelled'
      } else if (this.reservation.refunded) {
        textClass = 'text-danger'
        icon = 'fa-exchange-alt'
        text = 'Refunded'
      }
      return { class: textClass, icon, text }
    },
    reservation () {
      return {
        uuid: '',
        Lot: {},
        Account: {},
        Car: {},
        AdminResponsible: {},
        FinanceCode: false,
        ReservationPromo: false,
        ...this.data
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/displays/ReservationDisplay.vue